import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { MessageCircle, Receipt, Circle, Banknote, CreditCard, ArrowLeftRight } from 'lucide-react';
import { MODALIDAD_OPTIONS } from '../../utils/calendarConstants';

const modalidadInfo = (modalidad) =>
  MODALIDAD_OPTIONS.find(o => o.value === modalidad) || MODALIDAD_OPTIONS[0];

const METODOS_PAGO = [
  { value: 'efectivo',      label: 'Efectivo',      icon: Banknote },
  { value: 'tarjeta',       label: 'Tarjeta',       icon: CreditCard },
  { value: 'transferencia', label: 'Transferencia', icon: ArrowLeftRight },
];

const metodoLabel = (metodo) =>
  METODOS_PAGO.find(m => m.value === metodo)?.label || '';

const ANTICIPO_METODO_LABEL = { mercado_pago: 'Mercado Pago', transferencia: 'Transferencia' };

// El pago para confirmar la cita (lo registra el bot) se descuenta de la consulta.
const restaPorCobrar = (costo, appointment) =>
  Math.max(costo - (appointment.anticipo_monto || 0), 0);

const buildWhatsappLink = (clinicPhone, appointment) => {
  const digits = (clinicPhone || '').replace(/\D/g, '');
  const mensaje =
    `Hola, tengo cita de ${modalidadInfo(appointment.modalidad).label.toLowerCase()} ` +
    `el ${appointment.fecha} a las ${appointment.hora_inicio}. ` +
    `Paciente: ${appointment.paciente_nombre}.`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(mensaje)}`;
};

const generarReciboPDF = (appointment, clinicName) => {
  const info = modalidadInfo(appointment.modalidad);
  const costo = appointment.costo ?? info.costo;
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text(clinicName || 'Recibo de pago', 14, 18);
  doc.setFontSize(11);
  doc.text('Recibo de pago', 14, 28);

  const rows = [
    ['Paciente', appointment.paciente_nombre || ''],
    ['Doctor', appointment.doctor_nombre || ''],
    ['Fecha', appointment.fecha],
    ['Hora', `${appointment.hora_inicio} - ${appointment.hora_fin}`],
    ['Modalidad', info.label],
    ['Costo', `$${costo} MXN`],
    ...(appointment.anticipo_monto
      ? [
          ['Pago para confirmar', `$${appointment.anticipo_monto} MXN (${ANTICIPO_METODO_LABEL[appointment.anticipo_metodo] || ''})`],
          ['Resta por cobrar', `$${restaPorCobrar(costo, appointment)} MXN`],
        ]
      : []),
    ['Estatus', appointment.pagado ? 'Pagado' : 'Pendiente de pago'],
    ['Forma de pago', appointment.pagado ? metodoLabel(appointment.metodo_pago) || 'No especificada' : '—'],
  ];

  let y = 42;
  rows.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(`${label}:`, 14, y);
    doc.setFont(undefined, 'normal');
    doc.text(String(value), 60, y);
    y += 8;
  });

  doc.save(`recibo-${appointment.paciente_nombre || 'cita'}-${appointment.fecha}.pdf`);
};

/**
 * Bloque de pago para el detalle de una cita: muestra modalidad/costo/estatus,
 * permite marcar pagado, generar QR de WhatsApp y descargar el recibo en PDF.
 */
export const ConsultaPagoPanel = ({ appointment, clinicPhone, clinicName, onTogglePago }) => {
  const [qrOpen, setQrOpen] = useState(false);
  const info = modalidadInfo(appointment.modalidad);
  const costo = appointment.costo ?? info.costo;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Badge variant="outline">{info.label}</Badge>
        <span className="text-sm font-medium">${costo} MXN</span>
        <Badge
          className={appointment.pagado
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'}
        >
          {appointment.pagado
            ? `Pagado${appointment.metodo_pago ? ` · ${metodoLabel(appointment.metodo_pago)}` : ''}`
            : 'Pendiente de pago'}
        </Badge>
      </div>

      {appointment.anticipo_monto ? (
        <div
          className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm dark:border-sky-900 dark:bg-sky-950/40"
          data-testid="anticipo-info"
        >
          <p>
            Pago para confirmar: <span className="font-medium">${appointment.anticipo_monto} MXN</span>
            {appointment.anticipo_metodo && ` · ${ANTICIPO_METODO_LABEL[appointment.anticipo_metodo]}`}
          </p>
          {!appointment.pagado && (
            <p className="text-muted-foreground">
              Resta por cobrar: <span className="font-medium text-foreground">${restaPorCobrar(costo, appointment)} MXN</span>
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Sin pago para confirmar</p>
      )}

      <div className="grid grid-cols-1 gap-2">
        {appointment.pagado ? (
          <Button variant="outline" className="w-full" onClick={() => onTogglePago(appointment)}>
            <Circle className="h-4 w-4 mr-2" /> Marcar como no pagado
          </Button>
        ) : (
          // Cobrar y registrar la forma de pago en un solo clic.
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Registrar pago con:</p>
            <div className="grid grid-cols-3 gap-2">
              {METODOS_PAGO.map(({ value, label, icon: Icon }) => (
                <Button
                  key={value}
                  variant="outline"
                  size="sm"
                  onClick={() => onTogglePago(appointment, value)}
                  data-testid={`pago-${value}`}
                >
                  <Icon className="h-4 w-4 mr-1" /> {label}
                </Button>
              ))}
            </div>
          </div>
        )}
        <Button variant="outline" className="w-full" onClick={() => setQrOpen(true)}>
          <MessageCircle className="h-4 w-4 mr-2" /> Generar QR de WhatsApp
        </Button>
        <Button variant="outline" className="w-full" onClick={() => generarReciboPDF(appointment, clinicName)}>
          <Receipt className="h-4 w-4 mr-2" /> Generar recibo
        </Button>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>QR de WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            {clinicPhone
              ? (
                <>
                  <QRCodeSVG value={buildWhatsappLink(clinicPhone, appointment)} size={220} />
                  <p className="text-xs text-muted-foreground text-center">
                    Escanea para abrir WhatsApp con el mensaje de la cita ya redactado.
                  </p>
                </>
              )
              : (
                <p className="text-sm text-muted-foreground text-center">
                  No hay un teléfono de la clínica configurado (CLINIC_PHONE).
                </p>
              )
            }
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
